if (!customElements.get('color-swatches')) {
  customElements.define(
    'color-swatches',
    class ColorSwatches extends HTMLElement {
      connectedCallback() {
        if (this._init) return;
        this._init = true;

        this.card = this.closest('.card-wrapper');
        this.buttons = Array.from(this.querySelectorAll('.card__swatch-btn'));
        this.label = this.querySelector('[data-selected-color]');
        this.sizeItems = Array.from(this.querySelectorAll('.card__size-item'));
        this.sizeList = this.querySelector('[data-size-list]');
        this.sizeEmptyState = this.querySelector('[data-size-empty-state]');
        this.sizeStatus = this.querySelector('[data-size-availability-status]');
        this.variants = this.variantData();
        this.slider = this.card?.querySelector('card-image-slider') || null;
        this.onClick = this.onClick.bind(this);
        this.onMediaChange = this.onMediaChange.bind(this);
        this.addEventListener('click', this.onClick);
        this.slider?.addEventListener('card-media-change', this.onMediaChange);

        this.updateColorAvailability();

        const initial = this.initialButton();
        if (initial) this.setActive(initial, false);
        else this.updateSizeAvailability('', false);
      }

      disconnectedCallback() {
        if (!this._init) return;
        this.removeEventListener('click', this.onClick);
        this.slider?.removeEventListener('card-media-change', this.onMediaChange);
        this._init = false;
      }

      variantData() {
        const data = this.querySelector('.card-variant-data');
        if (!data) return [];

        try {
          return JSON.parse(data.textContent).variants || [];
        } catch (error) {
          return [];
        }
      }

      updateColorAvailability() {
        if (!this.variants.length) return;

        const availableColors = new Set(
          this.variants.filter((variant) => variant.a).map((variant) => variant.c)
        );
        this.buttons.forEach((button) => {
          const color = button.dataset.colorValue || '';
          const available = availableColors.has(color);
          button.classList.toggle('card__swatch-btn--unavailable', !available);
          button.setAttribute('aria-label', available ? color : `${color} (esgotado)`);
        });
      }

      initialButton() {
        const currentMediaId = this.slider?.currentMediaId?.()
          || this.card?.querySelector('.card__slide')?.dataset.mediaId;
        const mediaMatch = this.buttonForMedia(currentMediaId);
        if (mediaMatch) return mediaMatch;

        const liquidSelection = this.buttons.find((button) => button.getAttribute('aria-pressed') === 'true');
        if (liquidSelection) return liquidSelection;

        const initialColor = this.dataset.initialColor;
        if (initialColor) {
          const colorMatch = this.buttons.find((button) => button.dataset.colorValue === initialColor);
          if (colorMatch) return colorMatch;
        }

        return this.buttons.length === 1 ? this.buttons[0] : null;
      }

      buttonForMedia(mediaId, preserveActive = false) {
        const id = String(mediaId || '');
        if (!id) return null;

        const matches = this.buttons.filter((button) => button.dataset.mediaId === id);
        if (!matches.length) return null;

        const active = matches.find((button) => button.getAttribute('aria-pressed') === 'true');
        const available = matches.find(
          (button) => !button.classList.contains('card__swatch-btn--unavailable')
        );
        if (preserveActive && active) return active;
        if (active && (!available || !active.classList.contains('card__swatch-btn--unavailable'))) {
          return active;
        }

        const initialColor = this.dataset.initialColor;
        const initial = initialColor
          ? matches.find((button) => button.dataset.colorValue === initialColor)
          : null;
        if (initial && (!available || !initial.classList.contains('card__swatch-btn--unavailable'))) {
          return initial;
        }

        return available || active || initial || matches[0];
      }

      onClick(event) {
        const button = event.target.closest('.card__swatch-btn');
        if (!button || !this.contains(button)) return;

        // Buttons live inside the card; prevent the parent link from firing.
        event.preventDefault();

        const { mediaId, mediaSrcset, mediaSrc, mediaAlt } = button.dataset;

        // A escolha de cor vai direto à foto da variante. A galeria continua
        // disponível para navegar por gesto ou pelas setas.
        if (this.slider && mediaId && this.slider.goToMediaId?.(mediaId, 'instant')) {
          this.setActive(button);
          return;
        }

        if (mediaSrcset) {
          const img = this.card?.querySelector('.card__media .media img');
          if (img) {
            img.srcset = mediaSrcset;
            if (mediaSrc) img.src = mediaSrc;
            if (typeof mediaAlt === 'string') img.alt = mediaAlt;
            if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
              img.animate([{ opacity: 0.55 }, { opacity: 1 }], {
                duration: 180,
                easing: 'ease-out',
              });
            }
          }
        }

        this.setActive(button);
      }

      onMediaChange(event) {
        const mediaId = String(event.detail?.mediaId || '');
        if (!mediaId) return;

        const match = this.buttonForMedia(mediaId, true);
        if (match) this.setActive(match);
      }

      setActive(button, announce = true) {
        this.buttons.forEach((el) =>
          el.setAttribute('aria-pressed', 'false')
        );
        button.setAttribute('aria-pressed', 'true');

        const color = button.dataset.colorValue || '';
        if (this.label) this.label.textContent = color;
        this.updateSizeAvailability(color, announce);
        this.card?.dispatchEvent(new CustomEvent('card-color-change', { detail: { color } }));

        const availableVariant = this.variants.find((variant) => variant.a && variant.c === color);
        const colorVariant = availableVariant || this.variants.find((variant) => variant.c === color);
        const variantUrl = colorVariant?.u || button.dataset.variantUrl;
        if (variantUrl) {
          this.card
            ?.querySelectorAll('.card__heading a, .card__slide-link')
            .forEach((link) => link.setAttribute('href', variantUrl));
        }
      }

      updateSizeAvailability(color, announce = true) {
        if (!this.sizeItems.length) return;

        const availableSizes = new Set(
          this.variants
            .filter((variant) => variant.a && (!color || variant.c === color))
            .map((variant) => variant.s)
        );
        const visibleSizes = [];

        this.sizeItems.forEach((item) => {
          const size = item.dataset.sizeValue || '';
          const available = availableSizes.has(size);
          item.hidden = !available;
          item.dataset.available = available ? 'true' : 'false';
          if (available) visibleSizes.push(size);
        });

        if (this.sizeEmptyState) this.sizeEmptyState.hidden = visibleSizes.length > 0;

        const colorContext = color ? ` para ${color}` : '';
        const result = visibleSizes.length ? visibleSizes.join(', ') : 'nenhum';
        const message = `Tamanhos disponíveis${colorContext}: ${result}`;
        this.sizeList?.setAttribute('aria-label', message);
        if (announce && this.sizeStatus) this.sizeStatus.textContent = message;
      }
    }
  );
}
